import { Injectable, inject } from "@angular/core";
import { FgdsProductType, Product } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { AlarmDeviceConfiguration } from "@domain/project/configurations/alarm-device-configuration";
import { AttachmentDataService } from "@domain/project/product-data/attachment-data.service";
import { ProductData } from "@domain/project/product-data/product-data";
import { ConfigProductMap } from "@domain/project/product-list/config-product-map";
import { ProductListDiscountSlot } from "@lib/dtos/project/product-list-discount.dto";
import { ALPHABET } from "@utils/alphabet";
import { Observable, catchError, defaultIfEmpty, filter, forkJoin, map, of } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class AlarmDeviceDataService {
  private productService = inject(ProductService);
  private attachmentDataService = inject(AttachmentDataService);

  assignProductsToConfiguration(
    configurations: readonly AlarmDeviceConfiguration[],
    uniqueConfigs: AlarmDeviceConfiguration[],
    localeId: string,
  ): Observable<ConfigProductMap[]> {
    if (uniqueConfigs.length === 0) {
      return of([]);
    }
    uniqueConfigs.sort((a, b) => parseInt(a.positionNumber!) - parseInt(b.positionNumber!));
    return forkJoin(
      uniqueConfigs.map((uniqueConfiguration) =>
        this.getProducts(uniqueConfiguration, localeId).pipe(
          map((productData) => {
            const placedProductsCount: number = configurations.filter(
              (configuration) => configuration.id === uniqueConfiguration.id,
            ).length;
            return new ConfigProductMap(uniqueConfiguration, productData, placedProductsCount);
          }),
        ),
      ),
    );
  }

  private getProducts(alarmDeviceConfig: AlarmDeviceConfiguration, localeId: string): Observable<ProductData[]> {
    const attachmentStartingIndex = alarmDeviceConfig.productIds.length > 1 ? alarmDeviceConfig.productIds.length : 0;

    const alarmDevicesProductData$: Observable<ProductData[]> = this.getAlarmDeviceProducts(alarmDeviceConfig).pipe(
      map((products) => {
        return products.map((product, index) => {
          const productData = ProductData.create(
            product,
            localeId,
            this.productService.getFilterCountry()!,
            1,
            ProductListDiscountSlot.MAIN,
          );
          productData.positionId = alarmDeviceConfig.positionNumber!;

          if (products.length > 1) {
            productData.positionId += "." + ALPHABET[index];
          }
          return productData;
        });
      }),
    );

    const attachmentsProductData$ = this.attachmentDataService.getProductData(
      alarmDeviceConfig,
      alarmDeviceConfig.attachments,
      localeId,
      attachmentStartingIndex,
    );

    return forkJoin([alarmDevicesProductData$, attachmentsProductData$]).pipe(
      map((results: any[]) => {
        const alarmDevicesData: ProductData[] = [...results[0], ...results[1]];
        return alarmDevicesData;
      }),
    );
  }

  private getAlarmDeviceProducts(alarmDeviceConfig: AlarmDeviceConfiguration): Observable<Product[]> {
    const observables = alarmDeviceConfig.productIds.map((productId) =>
      this.productService.getProductById(productId).pipe(
        filter((p): p is Product => !!p && p.type === FgdsProductType.ALARMDEVICE),
        defaultIfEmpty(undefined),
        catchError(() => {
          return of(undefined); // ensure forkJoin still completes
        }),
      ),
    );
    return forkJoin(observables).pipe(
      map((products): Product[] =>
        products.filter((product): product is Product => !!product && product.type === FgdsProductType.ALARMDEVICE),
      ),
    );
  }
}
